import { Navigate, useParams } from "react-router-dom";
import { Link } from "@/components/LocalLink";
import { LegacyShell, usePageMeta } from "@/components/PageShell";
import { POLICIES, POLICY_LINKS, CONTACT_EMAIL } from "@/data/policies";

const PolicyNav = ({ current }) => (
  <nav className="flex flex-wrap gap-x-6 gap-y-2" data-testid="policy-nav">
    {POLICY_LINKS.map(([label, href]) => (
      <Link
        key={href}
        to={href}
        data-testid={`policy-nav-${href.split("/").pop()}`}
        className={`text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
          href.endsWith(current) ? "text-[#B5A642]" : "text-[#D3CEC4]/50 hover:text-[#E8DDC7]"
        }`}
      >
        {label}
      </Link>
    ))}
  </nav>
);

export default function Policy() {
  const { slug } = useParams();
  const policy = POLICIES[slug];

  usePageMeta(policy ? `${policy.title} · Dirty Paws Apothecary` : "Dirty Paws Apothecary", policy ? policy.lede : "");

  if (!policy) return <Navigate to="/" replace />;

  return (
    <LegacyShell testid={`policy-page-${slug}`}>
      <main className="max-w-4xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <p className="font-display italic text-[#B5A642]">The Fine Print</p>
        <h1 className="mt-3 font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#E8DDC7]" data-testid="policy-title">
          {policy.title}
        </h1>
        <p className="mt-6 text-base md:text-lg text-[#D3CEC4]/65 leading-relaxed max-w-2xl">{policy.lede}</p>
        <div className="mt-10"><PolicyNav current={slug} /></div>
        <div className="hairline mt-10" />

        <div className="mt-12 space-y-12">
          {policy.sections.map((s, i) => (
            <section key={s.heading} className="grid md:grid-cols-12 gap-4 md:gap-10">
              <h2 className="md:col-span-4 font-display text-2xl text-[#E8DDC7]">
                <span className="font-ledger text-[11px] text-[#B5A642]/70 mr-3 align-middle">{String(i + 1).padStart(2, "0")}</span>
                {s.heading}
              </h2>
              <div className="md:col-span-8 space-y-4">
                {s.body.map((p) => (
                  <p key={p.slice(0, 24)} className="text-[15px] leading-relaxed text-[#D3CEC4]/75">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border border-[#303B2D] rounded-sm p-7 bg-[#161918]">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]">Write to the house</p>
          <p className="mt-3 text-[15px] text-[#D3CEC4]/70 leading-relaxed">
            Questions about an order, a return or your information? Write to{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="policy-contact-email" className="text-[#B5A642] hover:text-[#E8DDC7] transition-colors">
              {CONTACT_EMAIL}
            </a>{" "}
            and a human (supervised by a dog) will reply.
          </p>
          <p className="mt-4 font-ledger text-[11px] text-[#D3CEC4]/40">Last updated June 2026</p>
        </div>
      </main>
    </LegacyShell>
  );
}
